/**
 * §17.11 optical-vs-geometric alignment corrections — derived rules, not taste
 * values. Each correction is rounded to 0.5px so it survives DPR 2 (§17.11's
 * own framing note).
 */

/**
 * Case 1 — play glyph in a circular transport.
 *
 * A right-pointing triangle's area centroid sits at one third of its width
 * from the flat edge, so bounding-box centring (what `align-items: center`
 * does) puts the glyph's visual mass left of the circle's centre. The
 * correction is derived, not measured per instance: `+0.07 × glyphWidth`,
 * rounded to the nearest 0.5px.
 *
 * §17.11's own worked table: +1px at a 14px glyph, +1.5px at 20px, +2px at
 * 28px — this function reproduces exactly those three values.
 */
export function playGlyphOffset(glyphWidth: number): number {
  return Math.round(glyphWidth * 0.07 * 2) / 2
}

/**
 * Case 4 — icon beside a label.
 *
 * A flex row with `align-items: center` centres an inline icon on the
 * label's LINE box, not on its cap band. The line box extends below the
 * baseline by the descender, so line-box centring reads as lowering the
 * icon by roughly half of that — the icon and the word no longer share a
 * visual centre, even though every box in the row is geometrically centred.
 * The fix in §17.11's own words: "centre on the 0.73em cap band, not the
 * em box" — i.e. nudge the icon up, not re-derive the row's alignment.
 *
 * §17.11's worked table gives two points, keyed to the LABEL's font-size in
 * px (its two examples are 16 and 19 — this codebase's own
 * `--ls-type-body-size` and `--ls-type-lead-size`/`--ls-type-h4-size`):
 * ≈ −1px at 16, ≈ −1.5px at 19 (its own "≈", not an exact-reproduction
 * table the way case 1's is). Banded per the nearest documented tier,
 * matching `numeral-disc.tsx`'s `opticalOffsetPx` — same shape of problem
 * (an asymmetric glyph, centred on the wrong box), same fix shape (a
 * diameter/size-banded 0.5px-rounded nudge rather than a per-instance
 * taste value).
 */
export function iconLabelOffset(labelFontSizePx: number): number {
  if (labelFontSizePx <= 16) return -1
  if (labelFontSizePx <= 19) return -1.5
  return -2
}
